import dayjs from 'dayjs';
import { t } from 'i18next';
import * as yup from 'yup';

import { getEmptyErrorMessage, numberSchemaWithTransform } from 'core/yup';
import {
  findGapItemIndex,
  findLastBoundedItemIndex,
  findOverlappingItemIndex,
} from 'corporate/utils';

const isNotPastDay = (value: unknown) => {
  if (!value) {
    return true;
  }

  return !dayjs(value as Parameters<typeof dayjs>[0]).isBefore(dayjs(), 'day');
};

const isAfterStartDate = (endValue: unknown, startValue: unknown) => {
  if (!endValue || !startValue) {
    return true;
  }

  return dayjs(endValue as Parameters<typeof dayjs>[0]).isAfter(
    dayjs(startValue as Parameters<typeof dayjs>[0]),
    'day'
  );
};

const costItemSchema = () =>
  yup.object().shape({
    id: yup.number().nullable(),
    minQuantity: numberSchemaWithTransform()
      .label(t('labelMinQty'))
      .required(getEmptyErrorMessage(t('labelMinQty'))),
    maxQuantity: numberSchemaWithTransform()
      .label(t('labelMaxQty'))
      .nullable()
      .test('max-greater-than-min', t('messageMaxQtyGreaterThanMinQty'), function (value) {
        const { minQuantity } = this.parent;

        if (value == null || minQuantity == null) {
          return true;
        }

        return Number(value) > Number(minQuantity);
      }),
    cost: numberSchemaWithTransform()
      .label(t('labelCost'))
      .required(getEmptyErrorMessage(t('labelCost'))),
  });

export const upsertCorporatePartCostSchema = () =>
  yup.object().shape({
    startDate: yup
      .mixed()
      .label(t('labelStartDate'))
      .required(getEmptyErrorMessage(t('labelStartDate')))
      .test('start-date-not-past', t('messageStartDateMustBeTodayOrFuture'), isNotPastDay),
    endDate: yup
      .mixed()
      .label(t('labelEndDate'))
      .nullable()
      .test('end-date-after-start', t('messageEndDateMustBeAfterStartDate'), function (value) {
        return isAfterStartDate(value, this.parent.startDate);
      }),
    uom: yup
      .object()
      .label(t('labelUom'))
      .nullable()
      .required(getEmptyErrorMessage(t('labelUom'))),
    costItems: yup
      .array()
      .of(costItemSchema())
      .min(1, t('messageAtLeastOneCostItemRequired'))
      .test('no-overlap', '', function (items) {
        if (!items || items.length < 2) {
          return true;
        }

        const conflictingIndex = findOverlappingItemIndex(items);

        if (conflictingIndex == null) {
          return true;
        }

        return this.createError({
          path: `${this.path}[${conflictingIndex}].minQuantity`,
          message: t('messageQuantityRangesOverlap'),
        });
      })
      .test('no-gap', '', function (items) {
        if (!items || items.length < 2) {
          return true;
        }

        const conflictingIndex = findGapItemIndex(items);

        if (conflictingIndex == null) {
          return true;
        }

        return this.createError({
          path: `${this.path}[${conflictingIndex}].minQuantity`,
          message: t('messageQuantityRangesCannotHaveGap'),
        });
      })
      .test('last-max-empty', '', function (items) {
        const conflictingIndex = findLastBoundedItemIndex(items ?? []);

        if (conflictingIndex == null) {
          return true;
        }

        return this.createError({
          path: `${this.path}[${conflictingIndex}].maxQuantity`,
          message: t('messageLastMaxQuantityMustBeEmpty'),
        });
      }),
  });
